/**
 * E4 five phases (ธาตุ ไม้ ไฟ ดิน ทอง น้ำ): a second axis next to the natures (data/elements.ts, shown as ลักษณะ).
 * Phases sit on ARTS only, never on neigong. No flat duel damage bonus: overcoming lengthens an opening after a skilled
 * action, generating rewards a timed art switch, PvE gives x1.15 against a monster of the overcome phase.
 * Design: docs/design/combat/2026-10-11-five-phases.md (option B).
 */
export type Phase = 'wood' | 'fire' | 'earth' | 'metal' | 'water';
export const PHASES: readonly Phase[] = ['wood', 'fire', 'earth', 'metal', 'water'];

/** Sim gate switch (D-queue section P: run the sim gate, no pair moves > 4 pts). */
export const FIVE_PHASES_ON = true;

export const PHASE_INFO: Readonly<Record<Phase, { name: string; glyph: string; color: string }>> = {
  wood: { name: 'ไม้', glyph: '木', color: '#86bf7a' },
  fire: { name: 'ไฟ', glyph: '火', color: '#e0583a' },
  earth: { name: 'ดิน', glyph: '土', color: '#c8a050' },
  metal: { name: 'ทอง', glyph: '金', color: '#e6e0c0' },
  water: { name: 'น้ำ', glyph: '水', color: '#5cb0e8' },
};

/** เกื้อ: wood feeds fire, fire earth, earth metal, metal water, water wood. */
export const GENERATES: Readonly<Record<Phase, Phase>> = {
  wood: 'fire',
  fire: 'earth',
  earth: 'metal',
  metal: 'water',
  water: 'wood',
};
/** ข่ม: wood earth, earth water, water fire, fire metal, metal wood. */
export const OVERCOMES: Readonly<Record<Phase, Phase>> = {
  wood: 'earth',
  earth: 'water',
  water: 'fire',
  fire: 'metal',
  metal: 'wood',
};

export const PHASE_RULES = {
  /** Seconds a broken / shattered / countered foe stays open longer when the attacker's phase overcomes. */
  openExtra: 0.2,
  /** Hit then switch within this many seconds. */
  switchWindow: 3,
  /** Next move of the generated phase: damage bonus, qi cost multiplier, cooldown seconds. */
  genDamage: 1.1,
  genQiMul: 0.5,
  genCooldown: 15,
  /** PvE: damage multiplier vs a monster of the overcome phase (never vs players). */
  pveMul: 1.15,
} as const;

/** One phase per art (24). */
export const ART_PHASE: Readonly<Record<string, Phase>> = {
  'breeze-sword': 'wood',
  'ink-slate': 'wood',
  'pulse-seal': 'wood',
  'one-cut': 'wood',
  'dragon-palm': 'wood',
  'heat-sabre': 'fire',
  'tongs-claw': 'fire',
  'cliff-push-sabre': 'fire',
  'quiet-palm': 'fire',
  'bell-fist': 'earth',
  'hum-palm': 'earth',
  'founder-sabre': 'earth',
  'drift-fist': 'earth',
  'lamp-hook-sabre': 'metal',
  'quick-draw-sabre': 'metal',
  'chain-hold': 'metal',
  'hidden-arts': 'metal',
  'cloud-palm': 'water',
  'snow-sword': 'water',
  'drift-sword': 'water',
  'listening-blade': 'water',
  'chorus': 'water',
};

/** Monster phases (data/monsters.ts ids); unlisted monsters have none. */
export const MONSTER_PHASE: Readonly<Record<string, Phase>> = {
  wolf: 'wood',
  bandit: 'metal',
  masked: 'metal',
  'masked-chief': 'metal',
  sparrer: 'earth',
  'sparrer-yard': 'earth',
  assassin: 'water',
  monk: 'earth',
  bear: 'earth',
  knifer: 'metal',
  jiangshi: 'earth',
  snowwolf: 'water',
  frostman: 'water',
  boar: 'earth',
  'boar-starter': 'earth',
  viper: 'wood',
  scorpion: 'fire',
  leopard: 'fire',
  bloodbat: 'fire',
  spider: 'wood',
  centipede: 'wood',
  python: 'water',
  foxspirit: 'fire',
  whitetiger: 'metal',
  boss: 'fire',
  worldboss: 'earth',
};

export const artPhase = (artId: string): Phase | undefined => ART_PHASE[artId];
export const monsterPhase = (defId: string): Phase | undefined => MONSTER_PHASE[defId];
/** True when `a` overcomes `b`. */
export const overcomes = (a: Phase | undefined, b: Phase | undefined): boolean => !!a && !!b && OVERCOMES[a] === b;
/** True when switching from `from` to `to` follows the generating cycle. */
export const generates = (from: Phase | undefined, to: Phase | undefined): boolean => !!from && !!to && GENERATES[from] === to;
/** UI label of the old five natures (หยิน อ่อน ไทเก๊ก หยาง แข็ง): ลักษณะ; ธาตุ is the five phases. */
export const NATURE_LABEL = 'ลักษณะ';
